import { createFileRoute } from "@tanstack/react-router";
import {
  isDossierStep,
  toDossierView,
  type DossierView,
} from "@modules/applications/_shared/types/step";
import {
  DossierFoundationScreen,
  ReviewScreen,
} from "@modules/applications/views";

interface ApplicationSearch {
  /** Which tab (or `review`) is open. Absent/invalid falls back to `founder` — never a blank screen. */
  step: DossierView;
}

/**
 * `/applicant/applications/$applicationId` — the canonical dossier URL.
 *
 * Inherits the applicant group guard from the `/applicant` layout route, so it declares no
 * `beforeLoad` of its own. It deliberately does NOT gate on `email_verified` either: by the time
 * anyone reaches a dossier they are verified (that is what creates it), and adding a second
 * verification gate here would only invent a way to lock someone out of a draft they own.
 *
 * The path is the integration guide's, not a preference — `/applicant/...` is on IAM's safe-`next`
 * whitelist for the applicant surface, so an expired session sent through login returns to this
 * exact dossier instead of a generic home.
 *
 * `?step=` mirrors the round builder's own `?step=` on `/staff/rounds/$roundId/setup` — same
 * reason: a reload or a shared link should land on the tab it was on, not always reset to Founder.
 * `toDossierView`/`isDossierStep` are imported from the FEATURE's `_shared/types/step` directly,
 * not any module barrel — `_shared`'s own barrel re-exports `keys.ts` (pulls React Query) and
 * `components` (pulls the rendering tree), so importing it here would cost exactly what this
 * file's whole point is to avoid. The round builder's route does the same for its own step type.
 *
 * `review` (US-E3-09..12) is a VIEW, not one of the four tabs — reached from Practical's own Next
 * button, not from `FoundationStepper`. `isDossierStep` is the same split the round builder's
 * `isWizardStep`/`BuilderView` already draws for the identical reason.
 */
export const Route = createFileRoute("/applicant/applications/$applicationId")({
  validateSearch: (search: Record<string, unknown>): ApplicationSearch => ({
    step: toDossierView(search.step),
  }),
  component: ApplicationRoute,
});

function ApplicationRoute() {
  const { applicationId } = Route.useParams();
  const { step } = Route.useSearch();
  const navigate = Route.useNavigate();

  // `replace: true` — moving between tabs (or into/out of review) is not a new place to land on
  // Back; it is the same dossier, further along. The round builder's wizard makes the identical
  // call for the same step param.
  const goTo = (next: DossierView) => {
    void navigate({ search: { step: next }, replace: true });
  };

  if (!isDossierStep(step)) {
    return (
      <ReviewScreen applicationId={applicationId} onNavigateToStep={goTo} />
    );
  }

  return (
    <DossierFoundationScreen
      applicationId={applicationId}
      step={step}
      onStepChange={goTo}
    />
  );
}
